import { createLogger } from 'shared/services/logger';
import us from '../../../defines';
import { hexColortoRGB, reverseCLR } from '../../../lib/utility/helpers';
import { createLayer } from '../layer';
import { get } from 'lodash';

const logger = createLogger('UScope:extensions:annotations:LayerManager');

// TODO / To Consider:
// - var isValidLayer_ = function (layerId) { return rfLayer[layerId] ? true : false; };
// - The layer manager should be the place where layers are created, saved, deleted, etc. However, not everything
//      uses the layer manager to create layers, which means it can get out of sync

export default function LayerManager (uScope, events, annotationsService) {
  const self = this;
  const thisRegions = annotationsService.getRegionsObj();

  // This array contains all layers, but layerId is not the index into this array. Note that the length of this array
  // is NOT the number of layers.  Deleted layers are undefined slots in the array.
  var allLayers = [];
  self.getLayersArray = function () {
    return allLayers;
  };
  // handy access into theLayers to the currently selected layer (or -1 if none selected)
  self.selectedIdx = -1;

  const findLayerByKeyAndValue = (key, val) => allLayers.find(layer => layer && layer.get(key) === val);

  self.getLayerById = layerId => findLayerByKeyAndValue('Id', layerId);

  self.getLayerIdxById = (layerId) => {
    const layer = self.getLayerById(layerId);
    return (layer) ? allLayers.indexOf(layer) : -1;
  };

  self.getLayerByName = name => findLayerByKeyAndValue('Name', name);

  self.getLayerIdByName = function (name) {
    const layer = self.getLayerByName(name);
    return (layer) ? layer.get('Id') : -1;
  };

  self.getLayerNameById = function (layerId) {
    var aLayer = self.getLayerById(layerId);
    return (aLayer) ? aLayer.get('Name') : undefined;
  };

  // TODO: Only return the ID? That seems oddly constraining.
  self.maybeCreateLayerAndGetId = async (name) => {
    let layerId = self.getLayerIdByName(name);
    if (layerId < 0) {
      const layer = await self.createAddSave({
        Name: name,
        Type: us.anLayerTypes.manual,
        LineColor: reverseCLR(hexColortoRGB(us.getRandomColor())),
        selected: true
      });
      layerId = layer.get('Id');
    }
    return layerId;
  };

  self.create = function (model) {
    model.Id = self.getNextLayerId(); // assign a negative Id to the newly created layer (will be re-assigned by DataServer and resolved on callback)
    model.ImageId = annotationsService.getMyImage().rawImageId;
    return createLayer(model, 'json'); // create the new layer
  };

  /**
     * This function creates a layer object from the model input, adds it to theLayers array and saves it to the database
     * @param model
     */
  self.createAdd = function (model) {
    const layer = self.create(model);

    // Try to add the layer to the tracking array.
    if (!self.add(layer)) {
      throw new Error('layer-manager::createAddSave() Failed to add the layer!');
    }

    return layer;
  };

  /**
     * This function creates a layer object from the model input, adds it to theLayers array and saves it to the database
     * @param model
     */
  self.createAddSave = function (model) {
    const layer = self.createAdd(model);

    return trySaveLayer(layer);
  };

  /**
     * This function adds the layer to the layers array and saves it to the database. Call it for layers newly created
     * layer objects.
     * @param layer (Object) - a layer object. Note that its Id should be assigned by layers.setNextLayerId_  and be negative.
     * Note also that the save callback will resolve the negative Id with that assigned by the database
     * @returns {Promise}
     */
  async function trySaveLayer (layer) {
    const RETRY_LIMIT = 5;

    // If the user can't save annotations, then we've done all we can. Return.
    if (!uScope.canSaveAnnotations()) {
      return Promise.resolve(layer);
    }

    let counter = 0;
    let succeeded = false;
    while (!succeeded && counter < RETRY_LIMIT) {
      try {
        counter++;
        await saveLayer(layer);
        succeeded = true;
      } catch (e) {
        logger.error(`Failed to save layer; attempt # ${counter}. Error:`, e);
      }
    }

    if (counter >= RETRY_LIMIT) {
      throw new Error(`Failed to save layer to DataServer. Giving up after ${counter} attempts.`);
    }

    return layer;
  }

  async function saveLayer (layer) {
    const layerXML = layer.buildXML();

    const response = await annotationsService.save({
      action: 'addLayer',
      layerXML,
      tag: layer.get('Id')
    });

    updateLayerFromServer(response, layer);

    return layer;
  }

  // ............................................ cLayers updateLayerFromServer
  // updateLayerFromServer - This function is a callback when a layer is saved to the database.
  //
  const updateLayerFromServer = function (serverResponse, layer) {
    const newLayerId = Number(get(serverResponse, '$.Id'));
    self.updateLayerId(layer, newLayerId);
  };

  self.updateLayerId = function (layer, newLayerId) {
    // track the highest value layer id
    setNextLayerId_(newLayerId);
    const oldLayerId = layer.get('Id');
    layer.set('Id', newLayerId);
    events.trigger('us:annotations:layer-saved', {oldLayerId, layer});
  };

  // ............................................ cLayers add
  // add - This function adds the passed layer to theLayers array.
  //
  self.add = function (aLayer) {
    if (aLayer.get('isSupported')) {
      // The original WV code did not render more than one report or annotation layer. This created inconsistencies in
      // the data model which introduced instability in the data stores. TODO: Figure out what regressions this causes.

      allLayers.push(aLayer);
      events.trigger('us:annotations:layer-added', aLayer);

      setNextLayerId_(aLayer.get('Id'));
      return true;
    } else { // layer not supported
      logger.error('add: layer type is not yet supported');
      return false;
    }
  };

  // ............................................ cLayers replace
  self.replace = function (aLayer) {
    var layerId = aLayer.get('Id');
    var idx = self.getLayerIdxById(layerId);
    if (idx != -1) {
      allLayers[idx] = aLayer;
      return true;
    }
    return false;
  };
  // ............................................ cLayers createAddSaveSpecial
  //    createAddSaveSpecial - This function creates a new special layer and adds it to the container array.
  //            It returns true if it was sucessfully created and false if not.
  //    layerName:  us.ReportLayerName | us.AnnotLayerName

  self.createAddSaveSpecial = function (layerName) {
    if (uScope.canAnnotate() == false) {
      logger.error('layer-manager::createAddSaveSpecial() called when annotations are disabled.');
      return Promise.reject(new Error('createAddSaveSpecial() called when annotations are disabled.'));
    }
    if (layerName == us.ReportLayerName) {
      if (self.hasReportLayer()) { // insure a report layer does not already exist
        return false;
      }
      return self.createAddSave({
        'Name': us.ReportLayerName,
        'Type': us.anLayerTypes.report,
        'LineColor': reverseCLR(hexColortoRGB(us.ReportLayerRGB)),
        'selected': true
      });
    } else if (layerName == us.AnnotLayerName) {
      if (self.hasWvAnnotationLayer()) { // insure a annotation layer does not already exist
        return false;
      }
      return self.createAddSave({
        'Name': us.AnnotLayerName,
        'Type': us.anLayerTypes.manual,
        'LineColor': reverseCLR(hexColortoRGB(us.AnnotLayerRGB)),
        'selected': true
      });
    } else {
      logger.error('layer-manager::createAddSaveSpecial() called for non-special layer');
      return Promise.reject(new Error('createAddSaveSpecial() called for non-special layer'));
    }
  };

  /**
     * Removes the requested layer from the viewport.
     * @param layerId
     */
  self.removeLayer = function (layerId) {
    thisRegions.removeAllFromLayer(layerId);
    const idx = self.getLayerIdxById(layerId);
    allLayers[idx] = undefined;

    events.trigger('us:annotations:layer-removed', layerId);
  };

  // ............................................ cLayers getSelected
  self.getSelected = function () {
    return (self.selectedIdx < 0) ? undefined : allLayers[self.selectedIdx];
  };
  // ............................................ cLayers getSelectedIdx
  self.getSelectedIdx = function () {
    return self.selectedIdx;
  };

  // ............................................ cLayers getSelectedProperty
  self.getSelectedProperty = function (propName) {
    return (self.selectedIdx < 0 || allLayers[self.selectedIdx] == undefined)
      ? undefined : allLayers[self.selectedIdx].get('propName');
  };
  // ............................................ cLayers getSelectedId
  self.getSelectedId = function () {
    return (self.selectedIdx < 0 || allLayers[self.selectedIdx] == undefined)
      ? -1 : allLayers[self.selectedIdx].get('Id');
  };
  // ............................................ cLayers selectByProperty
  //    This function sets the 'Selected' property of the layer whose property matches the passed value
  //        and clears the Selected property on all all other layers.
  //    Note that the layer object keeps its 'Selected' property up to date
  //        yet a property of the layers object keeps a handy access to its index

  self.selectByProperty = function (propName, propValue) {
    for (var lay = 0, len = allLayers.length; lay < len; lay++) {
      if (allLayers[lay] == undefined) { continue; }
      if (allLayers[lay].get(propName) == propValue) {
        allLayers[lay].set('Selected', true); // this is the layer that is selected
        self.selectedIdx = lay;
      } else {
        allLayers[lay].set('Selected', false); // no other layers can be selected
      }
    }

    events.trigger('us:annotations:layer-selected', allLayers[self.selectedIdx]);

    return self.selectedIdx;
  };

  // ............................................ cLayers findByProperty
  // This function returns an array of layer objects whose property matches the passed value
  self.findByProperty = function (propName, propValue) {
    var rtnArray = [];
    if (propValue != undefined) {
      for (var lay = 0, len = allLayers.length; lay < len; lay++) {
        if (allLayers[lay] && allLayers[lay].get(propName) == propValue) { rtnArray.push(allLayers[lay]); }
      }
    }
    return rtnArray;
  };

  // ............................................ cLayers deleteRegions
  // WARNING: DO NOT USE THIS FUNCTION. WHILE IT APPEARS CONVENIENT AND GENERAL PURPOSE IT CAN POTENTIALLY
  //          HUNDREDS OR THOUSANDS OF HTTP REQUESTS TO THE SERVER. IT IS CURRENTLY ONLY USED TO DELETE REPORT REGIONS
  //          WHICH IS A UNIQUE CASE. TODO: REWORK AND REMOVE.
  self.deleteRegions = function (fromLayerId) {
    thisRegions.delAllFromLayer(fromLayerId);
  };

  /**
     * returns the next id sequence that will be used when getNextLayerId() is called
     * @returns {number}
     */
  self.getProvisionalLayerId = () => {
    return -(lastLayerId + 1);
  };

  self.getReportLayer = function () {
    const candidates = allLayers.filter(layer => layer && layer.get('Type') === us.anLayerTypes.report);
    if (candidates.length > 1) {
      logger.warn('Multiple report layers found. This should not be possible.');
    }
    return candidates[0];
  };
  self.hasReportLayer = function () {
    return !!self.getReportLayer();
  };

  self.hasWvAnnotationLayer = function () {
    const candidates = allLayers.filter(layer => layer && layer.get('Name') === us.AnnotLayerName);
    if (candidates.length > 1) {
      logger.warn('Multiple annotation layers found. This should not have happened.');
    }
    return candidates[0];
  };

  // ............................................ cLayers get/set NextLayerId()
  var lastLayerId = 0; // last layer id; used to set id for new layer
  // Note that this isn't really the assigned layer id but a unique (to uscope) id prior to assignment
  self.getNextLayerId = function () {
    return -(++lastLayerId); // new layer id's are negative values so they are flagged for dataserver
  };
  var setNextLayerId_ = function (id) { //
    if (id >= 0 && id > lastLayerId) { lastLayerId = id; }
  };
}



// WEBPACK FOOTER //
// ./src/uScope/js/extensions/annotations/layer/layer-manager.js